import React from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, AlertCircle } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';

const SubscriptionBadge = () => {
  const { subscriptionInfo, currentWorkspace } = useWorkspace();
  if (!subscriptionInfo) return null;

  const { plan, subscription, subscription_expired, is_super_admin, workspace_comped_lifetime } = subscriptionInfo;
  if (is_super_admin) return null;

  // Invited members don't need to renew — the workspace owner's plan covers access
  const role = String(currentWorkspace?.user_role || '').toLowerCase();
  const canManageBillingUi = ['owner', 'admin'].includes(role);
  if (!canManageBillingUi) return null;

  if (workspace_comped_lifetime && plan?.name) {
    return (
      <span
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 text-sm font-medium"
        title="Complimentary plan for this workspace"
      >
        <CreditCard className="w-4 h-4" />
        {plan.name}
      </span>
    );
  }

  const status = subscription?.status;

  const trialDaysLeft = (trialEnd) => {
    if (!trialEnd) return null;
    const end = new Date(trialEnd);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const days = status === 'trial' && subscription?.trial_end ? trialDaysLeft(subscription.trial_end) : null;

  // Expired - prominent CTA (owners/admins of this workspace only)
  if (subscription_expired || status === 'expired') {
    return (
      <Link
        to="/billing"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 transition-colors text-sm font-medium"
      >
        <AlertCircle className="w-4 h-4" />
        <span>Expired – Renew</span>
      </Link>
    );
  }

  // Failed / past_due
  if (status === 'failed' || status === 'past_due') {
    return (
      <Link
        to="/billing"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors text-sm font-medium"
      >
        <AlertCircle className="w-4 h-4" />
        <span>Payment failed</span>
      </Link>
    );
  }

  // Trial
  if (status === 'trial' && days !== null) {
    return (
      <Link
        to="/billing"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors text-sm font-medium"
      >
        <CreditCard className="w-4 h-4" />
        <span>{days} day{days !== 1 ? 's' : ''} left</span>
      </Link>
    );
  }

  // Active - show plan name
  if (status === 'active' && plan?.name) {
    return (
      <Link
        to="/billing"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 transition-colors text-sm font-medium"
      >
        <CreditCard className="w-4 h-4" />
        <span>{plan.name}</span>
      </Link>
    );
  }

  return null;
};

export default SubscriptionBadge;
